import React, { useEffect, useRef, useState } from "react";
import { Send, X } from "lucide-react";
import { varcelSDK } from "../services/VarcelSDK";

const welcomeMessage =
  "Welcome. I am Reagan's AI Assistant. My name is Aaliyah. Ask me about Reagan's career, academic background, skills";

const formatChatContent = (content) =>
  content
    .replace(/```[\s\S]*?```/g, (match) =>
      match.replace(/```[a-zA-Z0-9-]*\n?/g, "").replace(/```/g, "")
    )
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)")
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^>\s?/gm, "")
    .replace(/^[-*+]\s+/gm, "")
    .replace(/^\s{0,3}[-*_]{3,}\s*$/gm, "")
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    .replace(/~~(.*?)~~/g, "$1")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const ChatBotModal = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      setMessages([]);
      setInput("");
      setIsProcessing(false);
      setError("");
      return;
    }

    setMessages([
      {
        id: "welcome",
        role: "assistant",
        content: welcomeMessage,
      },
    ]);
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isProcessing]);

  const sendMessage = async (event) => {
    event.preventDefault();

    const trimmedInput = input.trim();
    if (!trimmedInput || isProcessing) {
      return;
    }

    const userMessage = {
      id: `${Date.now()}-user`,
      role: "user",
      content: trimmedInput,
    };

    setMessages((currentMessages) => [...currentMessages, userMessage]);
    setInput("");
    setError("");
    setIsProcessing(true);

    try {
      const assistantMessageId = `${Date.now()}-assistant`;
      const nextMessages = [...messages, userMessage];
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: assistantMessageId,
          role: "assistant",
          content: "",
        },
      ]);

      const assistantResponse = await varcelSDK.streamCareerChat({
        messages: nextMessages.map(({ role, content }) => ({ role, content })),
        signal: abortController.signal,
        onToken: (token) => {
          setMessages((currentMessages) =>
            currentMessages.map((message) =>
              message.id === assistantMessageId
                ? { ...message, content: `${message.content}${token}` }
                : message
            )
          );
        },
      });

      if (assistantResponse) {
        setMessages((currentMessages) =>
          currentMessages.map((message) =>
            message.id === assistantMessageId && !message.content
              ? { ...message, content: assistantResponse }
              : message
          )
        );
      }
    } catch (err) {
      console.error("Error processing message:", err);
      if (err?.name !== "AbortError") {
        setError("I could not process that message. Please try again.");
      }
    } finally {
      abortControllerRef.current = null;
      setIsProcessing(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/75 px-4 backdrop-blur-sm">
      <div className="flex h-[min(720px,90vh)] w-full max-w-2xl flex-col rounded-2xl border border-cyan-400/25 bg-slate-900 shadow-2xl shadow-cyan-950/40">
        <div className="flex items-center justify-between gap-4 border-b border-slate-800 px-6 py-5">
          <div>
            <h2 className="text-xl font-semibold text-white">Reagan's AI Assistant</h2>
            <p className="mt-1 text-sm text-slate-400">Chat with Aaliyah</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-700 p-2 text-slate-300 transition hover:border-cyan-400 hover:text-cyan-300"
            aria-label="Close AI Assistant"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:px-6">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed sm:text-base ${
                  message.role === "user"
                    ? "bg-gradient-to-r from-cyan-500 to-purple-500 text-white"
                    : "border border-slate-700 bg-slate-950/70 text-slate-100"
                }`}
              >
                {message.content ? formatChatContent(message.content) : "Aaliyah is typing..."}
              </div>
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>

        {error && (
          <div className="mx-4 mb-4 rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200 sm:mx-6">
            {error}
          </div>
        )}

        <form onSubmit={sendMessage} className="border-t border-slate-800 p-4 sm:p-6">
          <div className="flex gap-3">
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              disabled={isProcessing}
              className="min-w-0 flex-1 rounded-xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-slate-100 outline-none transition placeholder:text-slate-500 focus:border-cyan-400 disabled:cursor-not-allowed disabled:opacity-60"
              placeholder="Ask Aaliyah a question..."
            />
            <button
              type="submit"
              disabled={isProcessing || !input.trim()}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 text-white transition hover:from-cyan-400 hover:to-purple-400 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Send message"
            >
              <Send size={20} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatBotModal;
